import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import useCallApi from "../../hooks/useCallApi";
import { Routes } from "../../routes/Routes";
import { useAlert } from "../../context/AlertContext";
import { useFormik } from "formik";
import directory from "../../validations/directory";
import DirectoryApi from "../../api/directory";
import { useContent } from "../../content/useContent";
import { useRTL } from "../../hooks/useLanguage/useRTL";

const useDirectoryContainer = (id?: any, isEdit: any = false) => {
  const navigate = useNavigate();
  const { callApi, loading } = useCallApi();
  const { setSuccessMessage } = useAlert();
  const { schema, pages, general } = useContent();
  const { isRTL } = useRTL();

  const { directories } = pages;
  const [pageLoading, setPageLoading] = useState(true);
  const [openDeleteImage, setOpenDeleteImage] = useState(false);

  const [deletedImageType, setDeletedImageType] = useState("");

  const [initialValues, setInitialValues] = useState({
    name: "",
    nameAr: "",
    address: "",

    status: true,
    longitude: 0,
    latitude: 0,
    POI: "",
    openingHours: "",
    openingHoursAr: "",

    website: "",
    phone: "",
    meta: "",
    metaAr: "",
    reservable: true,
    comingSoon: false,
    category: "",
  });

  const [images, setImages] = useState<any>([]);
  const [logo, setLogo] = useState<any>([]);

  const [center, setCenter] = useState({ lat: 0, lng: 0 });
  const [zoom, setZoom] = useState(5);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [selectedImage, setSelectedImage] = useState<any>(null);

  useEffect(() => {
    if (id) {
      setPageLoading(true);

      const API = isEdit
        ? DirectoryApi.getOneBoth(id)
        : DirectoryApi.getOne(id);

      callApi(API, (directory: any) => {
        if (isEdit) {
          setInitialValues({
            ...directory,
            name: directory.name.en,
            nameAr: directory.name.ar,

            openingHours: directory.openingHours.en,
            openingHoursAr: directory.openingHours.ar,

            meta: directory.meta?.en,
            metaAr: directory.meta?.ar,

            status: directory.status === "Available",
            reservable: directory.reservable === "Available",
          });
        } else {
          setInitialValues({
            ...directory,
            status: directory.status === "Available",
            reservable: directory.reservable === "Available",
          });
        }

        setImages(directory.images);
        setLogo([directory.logo]);
        setCenter({ lat: directory.latitude, lng: directory.longitude });

        setPageLoading(false);
      });
    } else {
      setPageLoading(false);
    }
  }, [id, isRTL]);

  const handleDelete = () => {
    callApi(DirectoryApi.delete(id), () => {
      setOpenDeleteDialog(false);
      navigate(Routes.directories);
      setTimeout(() => setSuccessMessage(directories.deleteDir), 1000);
    });
  };

  const handleSubmit = (values: any) => {
    let body = { ...values };

    delete body.id;
    delete body.createdAt;
    delete body.updatedAt;
    body.name = {
      en: values.name,
      ar: values.nameAr,
    };

    body.openingHours = {
      en: values.openingHours,
      ar: values.openingHoursAr,
    };

    body.meta = {
      en: values.meta,
      ar: values.metaAr,
    };

    delete body.nameAr;
    delete body.openingHoursAr;
    delete body.metaAr;
    delete body.loyaltyCategory;

    body.status = values.status ? "Available" : "Not Available";
    body.reservable = values.reservable ? "Available" : "Not Available";
    body.images = images.map((image: any) => {
      return { url: image.url, thumbnail: image.thumbnail };
    });
    body.logo = logo[0];

    if (values.POI === "") {
      delete body.POI;
    }

    const successMessage = id ? directories.updateDir : directories.createDir;

    if (id) {
      callApi(DirectoryApi.update(id, body), () => {
        setTimeout(() => setSuccessMessage(successMessage), 1000);
        navigate(Routes.getDirectory(id));
      });
    } else {
      callApi(DirectoryApi.create(body), (result: any) => {
        setTimeout(() => setSuccessMessage(successMessage), 1000);
        navigate(Routes.getDirectory(result.id));
      });
    }
  };

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: () => directory(schema),
    onSubmit: (values: any) => handleSubmit(values),
  });

  const handleMarker = (e: any) => {
    formik.setFieldValue("latitude", e.latLng.lat());
    formik.setFieldValue("longitude", e.latLng.lng());
  };

  const handleSearch = (lat: any, lng: any) => {
    formik.setFieldValue("latitude", lat);
    formik.setFieldValue("longitude", lng);
  };

  const handleFile = (event: any) => {
    const files = event.target.files;
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("images", files[i]);
    }
    callApi(DirectoryApi.upload(formData), async (data: any) => {
      setSuccessMessage(general.uploadImages);
      setImages([...images, ...data]);
    });
  };

  const handleLogo = (event: any) => {
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append("images", file);
    callApi(DirectoryApi.upload(formData), async (data: any) => {
      setSuccessMessage(directories.uploadLogo);
      setLogo(data);
    });
  };

  const handleOpenDeleteImage = (image: any) => {
    setOpenDeleteImage(true);
    setSelectedImage(image);
    setDeletedImageType("image");
  };

  const handleOpenDeleteLogo = (image: any) => {
    setOpenDeleteImage(true);
    setSelectedImage(image);
    setDeletedImageType("logo");
  };

  const handleDeleteImage = () => {
    if (selectedImage.id) {
      callApi(DirectoryApi.deleteImage(selectedImage.id), async (data: any) => {
        setSuccessMessage(general.deleteImage);
        if (deletedImageType === "logo") {
          setLogo([]);
        } else {
          setImages(
            images.filter((image: any) => image.id !== selectedImage.id)
          );
        }
        setSelectedImage({ id: "" });

        setOpenDeleteImage(false);
      });
    } else {
      callApi(
        DirectoryApi.deleteImageSource({ url: selectedImage.url }),
        async () => {
          setSuccessMessage(general.deleteImage);
          if (deletedImageType === "logo") {
            setLogo([]);
          } else {
            setImages(
              images.filter((image: any) => image.url !== selectedImage.url)
            );
          }
          setSelectedImage(null);

          setOpenDeleteImage(false);
        }
      );
    }
  };

  return {
    directory: initialValues,
    pageLoading,

    formik,
    center,
    handleMarker,
    handleSearch,
    setCenter,
    zoom,
    setZoom,

    handleDelete,
    openDeleteDialog,
    setOpenDeleteDialog,
    loading,

    handleFile,

    images,

    handleLogo,
    logo,

    openDeleteImage,
    setOpenDeleteImage,

    handleOpenDeleteImage,

    handleDeleteImage,
    handleOpenDeleteLogo,
  };
};

export default useDirectoryContainer;
